import React from 'react';
import { css } from '@emotion/css';
import { Theme, useTheme, TextField, InputAdornment, Typography } from '@mui/material';
import { BiUser, BiEnvelope, BiPhone } from 'react-icons/bi';
import { formatPhone } from '@services/formatService';

export interface PersonData {
  displayName: string;
  email: string;
  phone: string;
}

interface PersonStepProps {
  data: PersonData;
  onChange: (data: PersonData) => void;
  errors: Partial<Record<keyof PersonData, string>>;
}

const PersonStep: React.FC<PersonStepProps> = ({ data, onChange, errors }) => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const [phoneFocused, setPhoneFocused] = React.useState(false);

  const handleChange = (field: keyof PersonData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (field === 'phone') {
      const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
      onChange({ ...data, phone: digits });
    } else {
      onChange({ ...data, [field]: e.target.value });
    }
  };

  const phoneDisplay = !phoneFocused && data.phone ? (formatPhone(data.phone) ?? data.phone) : data.phone;

  return (
    <div>
      <Typography variant="overline" fontWeight={600} color="text.secondary" sx={{ mb: 2, display: 'block' }}>
        Person Details
      </Typography>

      <label className={cls.textFieldLabel}>
        Display Name <span className={cls.textFieldLabelRequired}>*</span>
      </label>
      <TextField
        placeholder="e.g. Jane Smith"
        size="small"
        fullWidth
        value={data.displayName}
        onChange={handleChange('displayName')}
        error={!!errors.displayName}
        helperText={errors.displayName}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <BiUser size={20} />
            </InputAdornment>
          ),
        }}
      />

      <div className={cls.spacer} />

      <label className={cls.textFieldLabel}>
        Email <span className={cls.textFieldLabelRequired}>*</span>
      </label>
      <TextField
        placeholder="e.g. jane@example.com"
        size="small"
        fullWidth
        type="email"
        value={data.email}
        onChange={handleChange('email')}
        error={!!errors.email}
        helperText={errors.email}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <BiEnvelope size={20} />
            </InputAdornment>
          ),
        }}
      />

      <div className={cls.spacer} />

      <label className={cls.textFieldLabel}>Phone</label>
      <TextField
        placeholder="e.g. (555) 123-4567"
        size="small"
        fullWidth
        value={phoneDisplay}
        onChange={handleChange('phone')}
        onFocus={() => setPhoneFocused(true)}
        onBlur={() => setPhoneFocused(false)}
        error={!!errors.phone}
        helperText={errors.phone}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <BiPhone size={20} />
            </InputAdornment>
          ),
        }}
      />
    </div>
  );
};

const useStyles = (theme?: Theme) => ({
  textFieldLabel: css`
    display: block;
    margin-bottom: 2px;
    margin-left: 6px;
    font-size: 12px;
    font-weight: 600;
  `,
  textFieldLabelRequired: css`
    color: ${theme?.palette?.error?.main};
  `,
  spacer: css`
    width: 100%;
    height: 12px;
  `,
});

export default PersonStep;
